<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMagicKeys } from '@vueuse/core'
import { loadStripe } from '@stripe/stripe-js'
import * as Sentry from '@sentry/vue'
import { RouterLinkBack, VButton, VButtonInvisible, VSection } from '@/modules/shared/components'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { useCustomerStore } from '../stores/customer-store'

const router = useRouter()
const route = useRoute()
const customerStore = useCustomerStore()

///////////////////////////////////////////////////////////////////////////////
// Keyboard shortcuts
///////////////////////////////////////////////////////////////////////////////

const { escape } = useMagicKeys()

watch(escape, (v) => {
  if (v) {
    router.push({ name: 'billing-group' })
  }
})

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

let stripe
let elements
const ready = ref(false)
const errorMessage = ref('')

const createBillable = (route) => ({
  id: route.params.billable_id !== undefined ? route.params.billable_id : customerStore.customer.metadata.group_id,
  type: route.params.billable_type !== undefined ? route.params.billable_type : 'group',
})

const billable = createBillable(route)
// If you disable collecting fields in the Payment Element, you
// must pass equivalent data when calling `stripe.confirmPayment`.
const handleSubmit = async () => {
  errorMessage.value = ''

  try {
    var url = window.location.href.split('add-payment-method')[0]

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: url,
        payment_method_data: {
          billing_details: {
            name: customerStore.customer.name,
            email: customerStore.customer.email,
          },
          metadata: {
            billables: `${billable.type}:${billable.id}`,
          },
        },
      },
    })

    if (error) {
      Sentry.captureException(error, {
        tags: { module: 'billing', action: 'confirm_setup' },
        extra: {
          error_type: error.type,
          error_code: error.code,
          decline_code: error.decline_code,
          billable_type: billable.type,
          user_agent: navigator.userAgent,
        },
      })
      errorMessage.value = error.message || 'Something went wrong. Please try again.'
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'billing', action: 'confirm_setup' },
      extra: {
        stripe_loaded: !!stripe,
        elements_loaded: !!elements,
        user_agent: navigator.userAgent,
      },
    })
    errorMessage.value = 'Something went wrong. Please try again.'
  }
}

// https://stripe.com/docs/payments/save-and-reuse
// https://stripe.com/docs/js/elements_object/create_payment_element
// https://stripe.com/docs/elements/appearance-api?platform=web#rules

onMounted(async () => {
  Sentry.addBreadcrumb({ category: 'billing', message: 'add-payment-method mounted' })

  try {
    await customerStore.retrieve()
    const setup_intent = await customerStore.retrieve_setup_intent(billable)

    if (!setup_intent?.client_secret) {
      errorMessage.value = 'Unable to initialize payment setup. Please try again or contact support.'
      return
    }

    Sentry.addBreadcrumb({
      category: 'billing',
      message: 'setup_intent retrieved',
      data: { has_client_secret: !!setup_intent?.client_secret },
    })

    stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

    Sentry.addBreadcrumb({ category: 'billing', message: 'stripe loaded', data: { success: !!stripe } })

    // Set up Stripe.js and Elements to use in checkout form, passing the client secret obtained in step 3
    elements = stripe.elements({
      clientSecret: setup_intent.client_secret,
      appearance: {
        labels: 'above',
        theme: 'stripe',
      },
    })

    const paymentElement = elements.create('payment', {
      fields: {
        billingDetails: {
          name: 'never',
          email: 'never',
        },
      },
      layout: {
        defaultCollapsed: true,
        radios: false,
        spacedAccordionItems: false,
        type: 'accordion',
      },
    })

    paymentElement.mount('#payment-element')

    Sentry.addBreadcrumb({ category: 'billing', message: 'payment element mounted' })
    ready.value = true
  } catch (err: any) {
    Sentry.captureException(err, {
      tags: { module: 'billing', action: 'mount_payment_element' },
      extra: { billable_type: billable.type, billable_id: billable.id },
    })
    console.error('[Billing] Failed to mount payment element:', err)
    errorMessage.value = 'Failed to load payment form. Please refresh and try again.'
  }
})

// Customize which fields are collected by the Payment Element
</script>

<template>
  <TheLayout>
    <!-- nav -->
    <VSection>
      <RouterLinkBack>
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </VSection>
    <!-- main -->
    <VSection label="Add Payment Method">
      <form id="payment-form" class="mb-6">
        <div id="payment-element">
          <!-- Elements will create form elements here -->
        </div>
        <div v-if="errorMessage" id="error-message" class="mt-2 text-sm text-red-600">
          {{ errorMessage }}
        </div>
      </form>
      <div class="space-x-2">
        <VButton id="submit" variant="primary" :click="handleSubmit" :disabled="!ready">Save for later</VButton>
        <RouterLinkBack>
          <VButton>Go back</VButton>
        </RouterLinkBack>
      </div>
    </VSection>
  </TheLayout>
</template>
